<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{
            myShiftPermission
              ? $t('leave.my_shift_permission_details')
              : $t('leave.shift_permission_details')
          }}
        </oxd-text>
      </div>
      <oxd-divider
        class="orangehrm-horizontal-margin orangehrm-clear-margins"
      />
      <br />
      <div v-if="request" class="orangehrm-horizontal-padding">
        <oxd-grid :cols="3">
          <oxd-grid-item>
            <oxd-input-group :label="$t('general.employee_name')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ employeeName }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-group :label="$t('general.type')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ request.typeName }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-group :label="$t('general.date')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ formattedDate }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-group :label="$t('leave.submitted_at')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ submittedAtDisplay }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item v-if="showExpectedTime">
            <oxd-input-group :label="expectedTimeLabel">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ expectedTimeDisplay }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-group :label="$t('general.status')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ request.statusName }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item v-if="request.reviewedBy">
            <oxd-input-group :label="$t('leave.reviewed_by')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ reviewerName }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item v-if="request.reviewedAt">
            <oxd-input-group :label="$t('leave.reviewed_at')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ reviewedAtDisplay }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
          <oxd-grid-item v-if="request.reviewComment">
            <oxd-input-group :label="$t('leave.review_comment')">
              <oxd-text class="orangehrm-request-details-text" tag="p">
                {{ request.reviewComment }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
        </oxd-grid>
      </div>
      <div class="orangehrm-bottom-container">
        <span>
          <oxd-button
            display-type="ghost"
            :label="$t('general.back')"
            @click="onClickBack"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            icon-name="chat-right-text-fill"
            :label="$t('general.comments')"
            @click="onClickComments"
          />
        </span>
      </div>
    </div>
  </div>
  <shift-permission-comments-modal
    v-if="showCommentModal"
    :id="Number(shiftPermissionRequestId)"
    @close="onCommentModalClose"
  />
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {buildAttendanceReportReturnUrl} from '@/orangehrmTimePlugin/util/attendanceReportReturnUrl';
import ShiftPermissionCommentsModal from '@/orangehrmLeavePlugin/components/ShiftPermissionCommentsModal';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {
  formatLocalDateTime12h,
  formatStoredUserDateTime,
  parseServerDateTime,
  TYPE_EARLY_LEAVE,
  TYPE_LATE_ARRIVAL,
} from '@/orangehrmLeavePlugin/util/shiftPermissionExpectedTime';

export default {
  name: 'ShiftPermissionRequest',

  components: {
    'shift-permission-comments-modal': ShiftPermissionCommentsModal,
  },

  props: {
    shiftPermissionRequestId: {
      type: String,
      required: true,
    },
    myShiftPermission: {
      type: Boolean,
      default: false,
    },
    returnUrl: {
      type: String,
      default: null,
    },
  },

  setup() {
    const {jsDateFormat} = useDateFormat();
    const {locale} = useLocale();
    const {$tEmpName} = useEmployeeNameTranslate();

    return {
      jsDateFormat,
      locale,
      translateEmpName: $tEmpName,
    };
  },

  data() {
    return {
      isLoading: false,
      request: null,
      showCommentModal: false,
    };
  },

  computed: {
    employeeName() {
      const employee = this.request?.employee;
      if (!employee) {
        return '';
      }
      return this.translateEmpName(employee, {
        includeMiddle: true,
        excludePastEmpTag: false,
      });
    },
    formattedDate() {
      if (!this.request?.date) {
        return '';
      }
      return formatDate(parseDate(this.request.date), this.jsDateFormat, {
        locale: this.locale,
      });
    },
    submittedAtDisplay() {
      const submittedAt = parseServerDateTime(this.request?.submittedAt);
      return submittedAt
        ? formatLocalDateTime12h(submittedAt)
        : this.request?.submittedAt || '-';
    },
    isEarlyLeave() {
      return this.request?.type === TYPE_EARLY_LEAVE;
    },
    isLateArrival() {
      return this.request?.type === TYPE_LATE_ARRIVAL;
    },
    expectedTimeLabel() {
      if (this.isEarlyLeave) {
        return this.$t('leave.expected_departure_time');
      }
      if (this.isLateArrival) {
        return this.$t('leave.expected_arrival_time');
      }
      return this.$t('leave.expected_arrival_or_departure');
    },
    showExpectedTime() {
      if (this.isEarlyLeave) {
        return Boolean(
          this.request?.expectedDepartureUserTime ||
            this.request?.expectedDepartureUtcTime,
        );
      }
      if (this.isLateArrival) {
        return Boolean(
          this.request?.expectedArrivalUserTime ||
            this.request?.expectedArrivalAt,
        );
      }
      return false;
    },
    expectedTimeDisplay() {
      if (this.isEarlyLeave) {
        const userDisplay = formatStoredUserDateTime(
          this.request?.expectedDepartureUserTime,
        );
        if (userDisplay) {
          return userDisplay;
        }
        const utc = parseServerDateTime(this.request?.expectedDepartureUtcTime);
        return utc
          ? formatLocalDateTime12h(utc)
          : this.request?.expectedDepartureUtcTime || '-';
      }
      if (this.isLateArrival) {
        const userDisplay = formatStoredUserDateTime(
          this.request?.expectedArrivalUserTime,
        );
        if (userDisplay) {
          return userDisplay;
        }
        const utc = parseServerDateTime(this.request?.expectedArrivalAt);
        return utc
          ? formatLocalDateTime12h(utc)
          : this.request?.expectedArrivalAt || '-';
      }
      return '-';
    },
    reviewedAtDisplay() {
      const reviewedAt = parseServerDateTime(this.request?.reviewedAt);
      return reviewedAt
        ? formatLocalDateTime12h(reviewedAt)
        : this.request?.reviewedAt || '-';
    },
    reviewerName() {
      const reviewer = this.request?.reviewedBy;
      if (!reviewer) {
        return '';
      }
      return this.translateEmpName(reviewer);
    },
  },

  beforeMount() {
    this.loadRequest();
  },

  methods: {
    loadRequest() {
      this.isLoading = true;
      const http = new APIService(
        window.appGlobal.baseUrl,
        '/api/v2/leave/shift-permission-requests',
      );
      http
        .get(this.shiftPermissionRequestId)
        .then((response) => {
          this.request = response.data?.data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onClickComments() {
      this.showCommentModal = true;
    },
    onCommentModalClose() {
      this.showCommentModal = false;
    },
    resolveReturnUrl() {
      if (!this.returnUrl) {
        return null;
      }
      if (
        this.returnUrl.includes('startDate=') ||
        this.returnUrl.includes('fromDate=')
      ) {
        return this.returnUrl;
      }
      const params = new URLSearchParams(window.location.search);
      const reportStart =
        params.get('reportStartDate') || params.get('startDate');
      const reportEnd = params.get('reportEndDate') || params.get('endDate');
      return buildAttendanceReportReturnUrl(reportStart, reportEnd);
    },
    onClickBack() {
      const returnUrl = this.resolveReturnUrl();
      if (returnUrl) {
        navigate(returnUrl);
        return;
      }
      navigate(
        this.myShiftPermission
          ? '/leave/viewMyEarlyLeave'
          : '/leave/viewEmployeeShiftPermissions',
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-bottom-container {
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}
.orangehrm-request-details-text {
  font-size: $oxd-input-control-font-size;
}
</style>
